// Type-only on purpose, as in `byline.ts`: the slice reads `window.config` at
// module load, which a unit test running in node does not have.
import type { BlogArticle } from '../../mock-data/types';

/**
 * The order and the pages of the blog's article list.
 *
 * The service lists a tenant's articles oldest first, every one of them in a
 * single answer, so an Autopilot brand's newest article ended up at the bottom
 * of a list that grows by one every couple of days. The list reads most
 * recently updated first, the date its Updated column shows, ten at a time.
 * Both are decided here, on the full list the page already holds: the tab
 * counts, the category counts and the byline all read that full list, so only
 * the rows the table renders are sorted and sliced.
 */

export const ARTICLES_PER_PAGE = 10;

/** Milliseconds since the epoch, or 0 (the oldest possible) when unreadable. */
const updatedTime = (article: Pick<BlogArticle, 'updatedAt'>): number => {
  const time = Date.parse(article.updatedAt);

  return Number.isNaN(time) ? 0 : time;
};

/**
 * Most recently updated first, so an article the owner (or Autopilot, or
 * Lumio) just wrote into is the first row. The comparison is on instants, not
 * strings: the service trims trailing zeros from the fractional seconds it
 * sends, which puts '…00.3Z' after '…00.31Z' in a string sort. Two articles
 * updated in the same instant fall back to their ids, so the order never
 * changes between two reads.
 */
export const latestUpdateFirst = <T extends Pick<BlogArticle, 'id' | 'updatedAt'>>(articles: ReadonlyArray<T>): T[] =>
  [...articles].sort((a, b) => updatedTime(b) - updatedTime(a) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));

export type ArticlePage<T> = {
  /** The rows of this page. */
  items: T[];
  /** 1-based, after clamping. */
  page: number;
  /** At least 1, so an empty list is one empty page. */
  pageCount: number;
  /** 1-based positions of the first and last row shown, 0 for an empty list. */
  rangeStart: number;
  rangeEnd: number;
  total: number;
};

/**
 * One page of `rows`. A requested page past the end shows the last page
 * instead: deleting the only article of the last page, or a re-read that
 * brings back fewer articles, must not leave the owner looking at an empty
 * table. The list page then adopts the clamped page as the one the owner is on.
 */
export const pageOf = <T>(rows: ReadonlyArray<T>, requested: number, size = ARTICLES_PER_PAGE): ArticlePage<T> => {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const page = Math.min(Math.max(1, Math.floor(requested)), pageCount);
  const start = (page - 1) * size;
  const items = rows.slice(start, start + size);

  return { items, page, pageCount, rangeStart: total === 0 ? 0 : start + 1, rangeEnd: start + items.length, total };
};
